import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken, getKuduUserId } from '@/api/legacyKuduAuth';
import {
  getAnnotationKind,
  isAnnotationTextColor,
  type Annotation,
  type NormalizedContentUnitAnnotationGroup,
} from '@/features/annotations/annotationTypes';
import { useAuth } from '@/auth/useAuth';
import {
  getScoreSummary,
  type ScoreSummary,
} from '@/features/content/stats/scoringUtils';
import {
  getComponentEndTimeUtcMilli,
  getComponentTimingState,
  type ComponentTimingState,
} from '@/features/content/stats/timingUtils';
import {
  normalizeQuestionSections,
  type NormalizedQuestionSectionData,
} from '@/features/questions/questionNormalizer';
import {
  resolveSectionAttempts,
  type SectionAttempts,
} from '@/features/questions/attemptSettings';
import { hashUserId } from '@/lib/hash/userIdHash';
import { parseAppletUrl, parseJupyterliteUrl } from '@/features/applets/appletOrigin';
import {
  getResizableImageSource,
  resolveImageUnitSrc,
  type ResizableImageSource,
} from './imageUnitSrc';
import { computeEffectiveDueDate } from './timeExtensions/useEffectiveDueDate';
import { extractAltFromCaption, resolveImageAltText } from './imageAltText';
import type { TimeExtensionGroup } from './timeExtensions/types';
import type { VideoSectionStatsMap } from './video/sentimentTypes';

export interface RawDateMessage {
  day?: number | null;
  month?: number | null;
  year?: number | null;
}

export interface RawIframeUnit {
  height?: number | string | null;
  source?: string | null;
  width?: number | string | null;
}

export interface RawTableUnit {
  density?: number | string | null;
  header_row?: boolean | null;
  headerRow?: boolean | null;
  html?: string | null;
  sticky_header?: boolean | null;
  stickyHeader?: boolean | null;
  style?: number | string | null;
}

export interface RawContentUnit {
  alt_text?: string | null;
  altText?: string | null;
  caption?: string | null;
  collapse_by_default?: boolean | null;
  collapseByDefault?: boolean | null;
  disable_auto_numbering?: boolean | null;
  disableAutoNumbering?: boolean | null;
  dynamic_image_url?: string | null;
  dynamicImageUrl?: string | null;
  heading?: string | null;
  id?: string | null;
  iframe_unit?: RawIframeUnit | null;
  iframeUnit?: RawIframeUnit | null;
  image_size?: number | string | null;
  image_url?: string | null;
  imageSize?: number | string | null;
  imageUrl?: string | null;
  table_unit?: RawTableUnit | null;
  tableUnit?: RawTableUnit | null;
  text?: string | null;
  type?: number | string | null;
  use_small_image?: boolean | null;
  useSmallImage?: boolean | null;
  video_id?: string | null;
  videoId?: string | null;
}

export interface RawContentSection {
  content_units?: RawContentUnit[] | null;
  contentUnits?: RawContentUnit[] | null;
  id?: number | string | null;
  uid?: number | string | null;
}

export interface RawAnnotationUnit {
  annotation?: string | null;
  end_offset?: number | string | null;
  endOffset?: number | string | null;
  id?: string | null;
  start_offset?: number | string | null;
  startOffset?: number | string | null;
  text?: string | null;
  text_color?: string | null;
  textColor?: string | null;
  text_prefix?: string | null;
  text_suffix?: string | null;
  textPrefix?: string | null;
  textSuffix?: string | null;
  timestamp?: number | string | null;
}

export interface RawContentUnitAnnotations {
  annotation_units?: RawAnnotationUnit[] | null;
  annotationUnits?: RawAnnotationUnit[] | null;
  content_unit_id?: number | string | null;
  contentUnitId?: number | string | null;
}

export interface RawVideoSection {
  id?: number | string | null;
  uid?: number | string | null;
  video_ids?: string[] | null;
  videoIds?: string[] | null;
}

export interface RawQuestionSection {
  id?: number | string | null;
  questions?: RawQuestion[] | null;
  stats?: RawSectionStats | null;
  uid?: number | string | null;
}

export interface RawAnswer {
  // The backend serializes these indices as strings in JSON (see getSelectedIndex).
  answer_index?: Array<number | string> | null;
  answer_index_stats?: Array<number | string> | null;
  answerIndex?: Array<number | string> | null;
  answerIndexStats?: Array<number | string> | null;
  // Credit an instructor gave by hand: a response in its own right.
  credit_given_manually?: boolean | null;
  creditGivenManually?: boolean | null;
  current_random_exact_answer?: string | null;
  currentRandomExactAnswer?: string | null;
  current_random_numerical_answer?: number | string | null;
  currentRandomNumericalAnswer?: number | string | null;
  current_random_numerical_answer_extra_exponent?: number | string | null;
  currentRandomNumericalAnswerExtraExponent?: number | string | null;
  exact_answer?: string | null;
  exactAnswer?: string | null;
  file_upload_url?: string | null;
  fileUploadUrl?: string | null;
  numerical_answer?: number | string | null;
  numericalAnswer?: number | string | null;
  numerical_answer_extra_exponent?: number | string | null;
  numericalAnswerExtraExponent?: number | string | null;
  // The student's resolved random-variable values, persisted with their answer
  // so the displayed variables and the graded current_random_numerical_answer
  // stay a matched pair even if the question's seed/prompt is later edited.
  // Absent on legacy answers, which fall back to client-side re-resolution.
  variables?: RawVariable[] | null;
}

export interface RawRubricUnit {
  details?: string | null;
  name?: string | null;
  score?: number | string | null;
}

export interface RawGradingRubric {
  rubric_units?: RawRubricUnit[] | null;
  rubricUnits?: RawRubricUnit[] | null;
  total_score?: number | string | null;
  totalScore?: number | string | null;
}

export interface RawGradingDetails {
  feedback?: string | null;
  manual_feedback?: string | null;
  manualFeedback?: string | null;
  upload_file_url?: string | null;
  uploadFileUrl?: string | null;
}

export interface RawVariableContentPair {
  answer?: string | null;
  answer_content?: RawContentSection | null;
  answer_numerical?: number | string | null;
  answer_string?: string | null;
  answerContent?: RawContentSection | null;
  answerNumerical?: number | string | null;
  answerString?: string | null;
  variable_content?: RawContentSection | null;
  variableContent?: RawContentSection | null;
}

export interface RawVariable {
  content_pairs?: RawVariableContentPair[] | null;
  contentPairs?: RawVariableContentPair[] | null;
  current_content_pair_index?: number | string | null;
  currentContentPairIndex?: number | string | null;
  current_value?: number | string | null;
  currentValue?: number | string | null;
  max_value?: number | string | null;
  maxValue?: number | string | null;
  min_value?: number | string | null;
  minValue?: number | string | null;
  name?: string | null;
  type?: number | string | null;
}

export interface RawQuestionSet {
  questions?: RawQuestion[] | null;
  total_score?: number | string | null;
  totalScore?: number | string | null;
}

export interface RawQuestion {
  answer_index?: Array<number | string> | null;
  answer_index_stats?: Array<number | string> | null;
  answerIndex?: Array<number | string> | null;
  answerIndexStats?: Array<number | string> | null;
  answer_units?: string | null;
  answerUnits?: string | null;
  author_uid?: string | null;
  authorUid?: string | null;
  content?: string | null;
  content_options?: RawContentSection[] | null;
  content_section?: RawContentSection | null;
  contentOptions?: RawContentSection[] | null;
  contentSection?: RawContentSection | null;
  credit_for_participation?: boolean | null;
  creditForParticipation?: boolean | null;
  current_user_uid?: string | null;
  currentUserUid?: string | null;
  // NOT the viewer's. These two sit on the shared question definition and can
  // hold another user's selection and attempt count (the retired frontend
  // saved them with the chapter). A viewer's answer and attempts are in their
  // own section_stats; nothing may read these as anyone's. Typed only because
  // stored chapters still carry them and the editor must drop them on save.
  current_answer?: RawAnswer | null;
  current_num_attempts?: number | string | null;
  currentAnswer?: RawAnswer | null;
  currentNumAttempts?: number | string | null;
  detailed_answer?: RawContentSection | null;
  detailedAnswer?: RawContentSection | null;
  do_not_override_max_num_attempts?: boolean | null;
  doNotOverrideMaxNumAttempts?: boolean | null;
  exact_answer?: string | null;
  exactAnswer?: string | null;
  full_hash?: number | string | null;
  fullHash?: number | string | null;
  grading_rubric?: RawGradingRubric | null;
  gradingRubric?: RawGradingRubric | null;
  hint?: RawContentSection | null;
  id?: number | string | null;
  image_paths?: string[] | null;
  imagePaths?: string[] | null;
  is_public?: boolean | null;
  isPublic?: boolean | null;
  max_num_attempts?: number | string | null;
  maxNumAttempts?: number | string | null;
  numerical_answer?: number | string | null;
  numerical_answer_extra_exponent?: number | string | null;
  numerical_error?: number | string | null;
  numericalAnswer?: number | string | null;
  numericalAnswerExtraExponent?: number | string | null;
  numericalError?: number | string | null;
  options?: string[] | null;
  parent_subject?: string | null;
  parentSubject?: string | null;
  question_set?: RawQuestionSet | null;
  questionSet?: RawQuestionSet | null;
  // Removed from the assignment by an instructor (SetQuestionRemoved): hidden
  // and not counted, but kept at its position because stats are positional.
  removed?: boolean | null;
  seed_for_random_variables?: string | null;
  seedForRandomVariables?: string | null;
  tags?: string[] | null;
  type?: number | string | null;
  uid?: number | string | null;
  user_free_hash?: number | string | null;
  userFreeHash?: number | string | null;
  use_custom_num_attempts?: boolean | null;
  use_random_variables?: boolean | null;
  useCustomNumAttempts?: boolean | null;
  useRandomVariables?: boolean | null;
  value?: number | string | null;
  variable_based_answer?: string | null;
  variableBasedAnswer?: string | null;
  variables?: RawVariable[] | null;
}

export interface RawQuestionSetStats {
  question_stats?: RawQuestionStats[] | null;
  questionStats?: RawQuestionStats[] | null;
}

export interface RawQuestionStats {
  answer?: RawAnswer | null;
  // Whether the submitted answer matched, recorded by the backend separately
  // from score because a question with participation credit scores full marks
  // however it was answered. Absent means the question has not been graded.
  answered_correctly?: boolean | null;
  answeredCorrectly?: boolean | null;
  current_num_attempts?: number | string | null;
  currentNumAttempts?: number | string | null;
  grading_details?: RawGradingDetails | null;
  gradingDetails?: RawGradingDetails | null;
  id?: number | string | null;
  // An instructor set this score by hand in the gradebook (SetQuestionScores).
  instructor_adjusted?: boolean | null;
  instructorAdjusted?: boolean | null;
  needs_grading?: boolean | null;
  needsGrading?: boolean | null;
  only_check_this_question?: boolean | null;
  onlyCheckThisQuestion?: boolean | null;
  question_set_stats?: RawQuestionSetStats | null;
  questionSetStats?: RawQuestionSetStats | null;
  save_only?: boolean | null;
  saveOnly?: boolean | null;
  score?: number | string | null;
}

export interface RawSectionStats {
  id?: number | string | null;
  num_attempts?: number | string | null;
  num_pre_lecture_attempts?: number | string | null;
  numAttempts?: number | string | null;
  numPreLectureAttempts?: number | string | null;
  pre_lecture_question_stats?: RawQuestionStats[] | null;
  preLectureQuestionStats?: RawQuestionStats[] | null;
  question_stats?: RawQuestionStats[] | null;
  questionStats?: RawQuestionStats[] | null;
  total_pre_lecture_score?: number | string | null;
  totalPreLectureScore?: number | string | null;
  total_score?: number | string | null;
  totalScore?: number | string | null;
  video_section_stats?: RawVideoSectionStats | null;
  videoSectionStats?: RawVideoSectionStats | null;
  // Kai Coach unit stats: an instructor set this score by hand.
  instructor_adjusted?: boolean | null;
  instructorAdjusted?: boolean | null;
}

export interface RawIntervalData {
  interval_length?: number | string | null;
  intervalLength?: number | string | null;
  sentiment?: Array<number | string | null> | null;
}

export interface RawVideoStats {
  cumulative_intervals?: RawIntervalData | null;
  cumulativeIntervals?: RawIntervalData | null;
  id?: string | null;
  normalized_intervals?: RawIntervalData | null;
  normalizedIntervals?: RawIntervalData | null;
}

export interface RawVideoSectionStats {
  id?: number | string | null;
  video_stats?: RawVideoStats[] | null;
  videoStats?: RawVideoStats[] | null;
}

export interface RawKuduUserProfile {
  [key: string]: unknown;
  first_name?: string | null;
  firstName?: string | null;
  is_instructor?: boolean | null;
  isInstructor?: boolean | null;
  last_name?: string | null;
  lastName?: string | null;
  preferred_email?: string | null;
  preferredEmail?: string | null;
}

export interface RawKuduUser {
  [key: string]: unknown;
  archived_course_ids?: string[] | null;
  archivedCourseIds?: string[] | null;
  course_ids?: string[] | null;
  courseIds?: string[] | null;
  dismissed_notifications?: Array<number | string> | null;
  dismissedNotifications?: Array<number | string> | null;
  id?: string | null;
  profile?: RawKuduUserProfile | null;
  uid?: string | null;
}

export interface RawQuestionSettings {
  allow_ai_assistant?: number | string | null;
  allow_ai_hints?: number | string | null;
  // Independent of allow_ai_hints. Unset means "follow allow_ai_hints" so that
  // chapters authored before the settings were split keep their behavior.
  allow_ai_practice_problems?: number | string | null;
  allowAiPracticeProblems?: number | string | null;
  // Attempts before an AI hint is offered. Unset or <= 0 means immediately.
  // Independent of num_attempts_to_show_hints_* (the authored-hint threshold).
  num_attempts_to_show_ai_hints?: number | string | null;
  numAttemptsToShowAiHints?: number | string | null;
  all_questions_participation_credit?: boolean | null;
  all_questions_participation_credit_weight?: number | string | null;
  allowAiAssistant?: number | string | null;
  allowAiHints?: number | string | null;
  allQuestionsParticipationCredit?: boolean | null;
  allQuestionsParticipationCreditWeight?: number | string | null;
  number_of_questions?: number | string | null;
  num_attempts_exact_answer?: number | string | null;
  num_attempts_multiple_choice?: number | string | null;
  num_attempts_to_show_hints_exact_answer?: number | string | null;
  num_attempts_to_show_hints_multiple_choice?: number | string | null;
  numberOfQuestions?: number | string | null;
  numAttemptsExactAnswer?: number | string | null;
  numAttemptsMultipleChoice?: number | string | null;
  numAttemptsToShowHintsExactAnswer?: number | string | null;
  numAttemptsToShowHintsMultipleChoice?: number | string | null;
  show_solutions_after_interval?: number | string | null;
  showSolutionsAfterInterval?: number | string | null;
  type?: number | string | null;
  user_defined_numbers?: Array<number | string> | null;
  userDefinedNumbers?: Array<number | string> | null;
}

export interface RawSection {
  content_section?: RawContentSection | null;
  contentSection?: RawContentSection | null;
  id?: number | string | null;
  kai_coach_section?:
    | import('@/features/kaiCoach/kaiCoachTypes').RawKaiCoachSection
    | null;
  kaiCoachSection?:
    | import('@/features/kaiCoach/kaiCoachTypes').RawKaiCoachSection
    | null;
  pre_lecture_question_section?: RawQuestionSection | null;
  preLectureQuestionSection?: RawQuestionSection | null;
  question_section?: RawQuestionSection | null;
  questionSection?: RawQuestionSection | null;
  stats?: unknown;
  uid?: number | string | null;
  video_section?: RawVideoSection | null;
  videoSection?: RawVideoSection | null;
}

export interface RawTimeExtensionCourseStudent {
  id?: number | string | null;
  uid?: number | string | null;
}

export interface RawTimeExtension {
  course_students?: RawTimeExtensionCourseStudent[] | null;
  courseStudents?: RawTimeExtensionCourseStudent[] | null;
  display_name?: string | null;
  displayName?: string | null;
  extension_end_time_utc_milli?: number | string | null;
  extensionEndTimeUtcMilli?: number | string | null;
}

export interface RawComponent {
  content_unit_annotations?: RawContentUnitAnnotations[] | null;
  contentUnitAnnotations?: RawContentUnitAnnotations[] | null;
  content_start_time_utc_milli?: string | null;
  contentStartTimeUtcMilli?: string | null;
  course_prelecture_question_settings?: RawQuestionSettings | null;
  course_question_settings?: RawQuestionSettings | null;
  coursePrelectureQuestionSettings?: RawQuestionSettings | null;
  courseQuestionSettings?: RawQuestionSettings | null;
  end_date?: RawDateMessage | null;
  end_time_utc_milli?: string | null;
  endDate?: RawDateMessage | null;
  endTimeUtcMilli?: string | null;
  homework_time_extensions?: RawTimeExtension[] | null;
  homeworkTimeExtensions?: RawTimeExtension[] | null;
  id?: number | string | null;
  is_complete?: boolean | null;
  is_live?: boolean | null;
  is_pre_lecture_complete?: boolean | null;
  is_pre_lecture_live?: boolean | null;
  is_section_divider?: boolean | null;
  isComplete?: boolean | null;
  isLive?: boolean | null;
  isPreLectureComplete?: boolean | null;
  isPreLectureLive?: boolean | null;
  isSectionDivider?: boolean | null;
  pre_lecture_end_time_utc_milli?: string | null;
  pre_lecture_time_extensions?: RawTimeExtension[] | null;
  pre_lecture_start_time_utc_milli?: string | null;
  pre_lecture_question_settings?: RawQuestionSettings | null;
  preLectureEndTimeUtcMilli?: string | null;
  preLectureTimeExtensions?: RawTimeExtension[] | null;
  preLectureStartTimeUtcMilli?: string | null;
  preLectureQuestionSettings?: RawQuestionSettings | null;
  question_settings?: RawQuestionSettings | null;
  questionSettings?: RawQuestionSettings | null;
  section_ids?: Array<number | string> | null;
  sectionIds?: Array<number | string> | null;
  section_stats?: RawSectionStats[] | null;
  sectionStats?: RawSectionStats[] | null;
  sections?: RawSection[] | null;
  start_date?: RawDateMessage | null;
  start_time_utc_milli?: string | null;
  startDate?: RawDateMessage | null;
  startTimeUtcMilli?: string | null;
  title?: string | null;
  uid?: number | string | null;
}

export interface RawGetCourseStudentComponentPayload {
  component?: RawComponent | null;
}

interface RawGetComponentPayload {
  component?: RawComponent | null;
}

interface RawGetSectionPayload {
  section?: RawSection | null;
}

// The course's question settings, for what a chapter leaves to its course.
export interface CourseQuestionDefaults {
  coursePrelectureQuestionSettings?: RawQuestionSettings;
  courseQuestionSettings?: RawQuestionSettings;
}

export interface ComponentPageData {
  classAveragePreLectureScore: number;
  classAverageScore: number;
  classQuestionScores: number[];
  /**
   * For a student, how many reads in a row, this one included, found their
   * homework closed and the chapter not yet marked complete (see
   * isChapterClosePending). The page reads again while it is set
   * (CLOSE_PENDING_READ_DELAYS_MS). Absent when the last read did not.
   */
  closePendingReads?: number;
  contentUnitAnnotations?: NormalizedContentUnitAnnotationGroup[];
  controlPanel: ControlPanelData;
  /**
   * Beside rawComponent, and never on it: a save sends rawComponent back, and
   * would store the course's settings on the chapter.
   */
  courseDefaults?: CourseQuestionDefaults;
  dateLabel: string;
  dueLabel?: string;
  hasInstructorAggregateStats: boolean;
  id: string;
  instructorStatsSections: InstructorStatsSectionData[];
  instructorUser?: RawKuduUser;
  isInstructorRequest: boolean;
  questionCount: number;
  questionSections: NormalizedQuestionSectionData[];
  rawComponent: RawComponent;
  scoreSummary: ScoreSummary;
  sections: NormalizedSection[];
  statusLabel: 'Complete' | 'Live' | 'Upcoming';
  timingState: ComponentTimingState;
  tocEntries: TocEntry[];
  title: string;
  videoIds: string[];
  videoSectionMap: Record<string, string>;
  videoSectionStats?: VideoSectionStatsMap;
}

export interface InstructorStatsSectionData {
  hasAggregateStats: boolean;
  homeworkQuestionCount: number;
  preLectureQuestionCount: number;
  sectionId: string;
  sectionStatsId?: string;
  totalPreLectureScore?: number;
  totalScore?: number;
}

export interface ControlPanelQuestionOption {
  id: string;
  index: number;
  previewText: string;
  questionIndexWithinSection?: number;
  sectionId?: string;
  sectionIndex?: number;
  typeLabel?: string;
}

export interface ControlPanelData {
  // The attempts in effect for each kind of question section, and where each
  // comes from: the chapter, the course, or the default (issue #54). Worked
  // out with the course defaults, which the raw chapter does not carry; the
  // panel's rows and the Question Settings dialogs read it. Always set by
  // normalizeComponentResponse.
  attempts?: { homework: SectionAttempts; preLecture: SectionAttempts };
  contentStartTimeUtcMilli?: string;
  hasHomeworkQuestionSection: boolean;
  hasPreLectureQuestionSection: boolean;
  homeworkAssignedIndexes: number[];
  homeworkEndTimeUtcMilli?: string;
  homeworkQuestionOptions: ControlPanelQuestionOption[];
  homeworkStartTimeUtcMilli?: string;
  homeworkTimeExtensions?: TimeExtensionGroup[];
  preLectureAssignedIndexes: number[];
  preLectureEndTimeUtcMilli?: string;
  preLectureQuestionOptions: ControlPanelQuestionOption[];
  preLectureStartTimeUtcMilli?: string;
  preLectureTimeExtensions?: TimeExtensionGroup[];
  preLectureQuestionSettings?: RawQuestionSettings;
  questionSettings?: RawQuestionSettings;
}

export interface TocEntry {
  id: string;
  label: string;
  level: 'example' | 'normal';
  numberedLabel?: string;
  type: 'content' | 'question' | 'video';
}

export const VIDEO_SECTION_ID = 'video-section';
export const QUESTION_SECTION_ID = 'question-section';

export function getContentUnitAnchorId(unitId: string) {
  return unitId.startsWith('content-unit-') ? unitId : `content-unit-${unitId}`;
}

interface ContentHeadingDisplay {
  includeInToc: boolean;
  level: TocEntry['level'];
  shouldNumber: boolean;
}

export interface NormalizedSection {
  id: string;
  units: NormalizedContentUnit[];
}

export interface BaseContentUnitData {
  anchorId?: string;
  caption?: string;
  collapseByDefault: boolean;
  disableAutoNumbering: boolean;
  heading?: string;
  id: string;
  kind: 'applet' | 'iframe' | 'image' | 'inlineVideo' | 'table' | 'text';
  numberedHeading?: string;
  summaryLabel: string;
}

export interface TextUnitData extends BaseContentUnitData {
  html: string;
  kind: 'text';
}

export interface ImageUnitData extends BaseContentUnitData {
  alt: string;
  kind: 'image';
  // The AUTHORED size, and therefore what renders first. A reader may display
  // the image at another size; that choice lives in the renderer and is never
  // written back here.
  size: 'large' | 'medium' | 'small';
  // The authored-size source. Also the fallback for images that cannot be
  // re-requested at another resolution.
  src: string;
  // What re-resolving `src` at a different displayed size needs. Absent when the
  // unit has only a raw storage URL, which must never be suffixed.
  resizableSource?: ResizableImageSource;
}

export interface IframeUnitData extends BaseContentUnitData {
  height: number;
  kind: 'iframe';
  src: string;
  title: string;
  widthPercent: number;
}

export interface AppletUnitData extends BaseContentUnitData {
  appletId: string;
  config: Record<string, unknown>;
  kind: 'applet';
  title: string;
  version: string;
  /** Path-based applet source (e.g. a JupyterLite content-site URL). */
  srcOverride?: string;
  /** Sandbox tokens this applet needs (default: allow-scripts only). */
  sandbox?: string;
  /** Fixed height in px for IDE-style applets. */
  fixedHeight?: number;
  /** When true, the host shows a confirmed reset-to-original control. */
  resettable?: boolean;
  /** Label for the reset control, e.g. "Reset notebook". */
  resetLabel?: string;
}

export interface InlineVideoUnitData extends BaseContentUnitData {
  embedUrl: string;
  kind: 'inlineVideo';
  title: string;
  videoId: string;
}

export type TableUnitDensity = 'comfortable' | 'compact';
export type TableUnitStyle = 'bordered' | 'plain' | 'striped';

export interface TableUnitData extends BaseContentUnitData {
  density: TableUnitDensity;
  // The authored <table> markup — the single source of truth. Rows and columns
  // are derived by the renderer, never stored alongside it.
  html: string;
  headerRow: boolean;
  kind: 'table';
  stickyHeader: boolean;
  style: TableUnitStyle;
}

export type NormalizedContentUnit =
  | AppletUnitData
  | IframeUnitData
  | ImageUnitData
  | InlineVideoUnitData
  | TableUnitData
  | TextUnitData;

interface NormalizeOptions {
  contentId: string;
  coursePrelectureQuestionSettings?: RawQuestionSettings;
  courseQuestionSettings?: RawQuestionSettings;
  instructorUser?: RawKuduUser;
  isInstructorRequest?: boolean;
  kuduUserId?: string;
}

interface UseComponentOptions {
  coursePrelectureQuestionSettings?: RawQuestionSettings;
  courseQuestionSettings?: RawQuestionSettings;
}

type RawContentUnitType = 'IFRAME' | 'IMAGE' | 'TABLE' | 'TEXT' | 'VIDEO';
type RawQuestionAssignmentType =
  | 'ALL'
  | 'FIXED'
  | 'RANDOM'
  | 'USER_DEFINED';

type RawQuestionType =
  | 'EXACT_ANSWER'
  | 'MULTI_PART'
  | 'MULTIPLE_CHOICE'
  | 'NUMERICAL_WITH_ERROR';

const CONTENT_UNIT_TYPE_MAP: Record<string, RawContentUnitType> = {
  '1': 'TEXT',
  '2': 'IMAGE',
  '3': 'VIDEO',
  '4': 'IFRAME',
  '5': 'TABLE',
  IFRAME: 'IFRAME',
  IMAGE: 'IMAGE',
  TABLE: 'TABLE',
  TEXT: 'TEXT',
  VIDEO: 'VIDEO',
};

const QUESTION_ASSIGNMENT_TYPE_MAP: Record<string, RawQuestionAssignmentType> = {
  '0': 'ALL',
  '1': 'ALL',
  '2': 'FIXED',
  '3': 'RANDOM',
  '4': 'USER_DEFINED',
  ALL: 'ALL',
  FIXED: 'FIXED',
  RANDOM: 'RANDOM',
  UNKNOWN: 'ALL',
  USER_DEFINED: 'USER_DEFINED',
};

const QUESTION_TYPE_MAP: Record<string, RawQuestionType> = {
  '0': 'MULTIPLE_CHOICE',
  '1': 'MULTIPLE_CHOICE',
  '3': 'NUMERICAL_WITH_ERROR',
  '4': 'EXACT_ANSWER',
  '5': 'MULTI_PART',
  EXACT_ANSWER: 'EXACT_ANSWER',
  MULTI_PART: 'MULTI_PART',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  NUMERICAL_WITH_ERROR: 'NUMERICAL_WITH_ERROR',
  UNKNOWN: 'MULTIPLE_CHOICE',
};

export const componentQueryKey = (
  courseId: string,
  contentId: string,
  isInstructorRequest = false,
  settingsKey?: string,
) =>
  settingsKey
    ? ([
        'component',
        courseId,
        contentId,
        isInstructorRequest,
        settingsKey,
      ] as const)
    : (['component', courseId, contentId, isInstructorRequest] as const);

function getQuestionSettingsCacheKey(options?: UseComponentOptions) {
  if (
    !options?.coursePrelectureQuestionSettings &&
    !options?.courseQuestionSettings
  ) {
    return undefined;
  }

  const settings = {
    coursePrelectureQuestionSettings: options?.coursePrelectureQuestionSettings,
    courseQuestionSettings: options?.courseQuestionSettings,
  };

  return JSON.stringify(settings);
}

function createDataError(message: string, status?: number): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  error.status = status;
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeTextQuoteValue(value: unknown): string {
  return typeof value === 'string' ? value.replace(/\u00a0/g, ' ') : '';
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeIdentifier(entry))
    .filter((entry): entry is string => Boolean(entry));
}

function toSectionArray(value: unknown): RawSection[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSection[];
}

function toSectionStatsArray(value: unknown): RawSectionStats[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSectionStats[];
}

function toTimeExtensionArray(value: unknown): RawTimeExtension[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawTimeExtension[];
}

function toTimeExtensionCourseStudentArray(
  value: unknown,
): RawTimeExtensionCourseStudent[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawTimeExtensionCourseStudent[];
}

function toVideoStatsArray(value: unknown): RawVideoStats[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawVideoStats[];
}

function normalizeTimeExtensions(value: unknown): TimeExtensionGroup[] {
  return toTimeExtensionArray(value)
    .map((extension) => {
      const endTimeUtcMilli = normalizeIdentifier(
        extension.extension_end_time_utc_milli ??
          extension.extensionEndTimeUtcMilli,
      );

      if (!endTimeUtcMilli) {
        return null;
      }

      const hashedStudentIds = Array.from(
        new Set(
          toTimeExtensionCourseStudentArray(
            extension.course_students ?? extension.courseStudents,
          )
            .map((student) =>
              normalizeIdentifier(student.id ?? student.uid),
            )
            .filter((studentId): studentId is string => Boolean(studentId)),
        ),
      );

      return {
        displayName:
          normalizeString(extension.display_name ?? extension.displayName) ?? '',
        endTimeUtcMilli,
        hashedStudentIds,
      } satisfies TimeExtensionGroup;
    })
    .filter((extension): extension is TimeExtensionGroup => extension !== null);
}

export function applyStudentTimeExtensions(
  component: RawComponent,
  currentUserHash: string | null,
): RawComponent {
  if (!currentUserHash) {
    return component;
  }

  const homeworkEffectiveDueDate = computeEffectiveDueDate({
    courseWideEndTimeUtcMilli: component.end_time_utc_milli ?? component.endTimeUtcMilli,
    currentUserHash,
    extensions: normalizeTimeExtensions(
      component.homework_time_extensions ?? component.homeworkTimeExtensions,
    ),
  });
  const preLectureEffectiveDueDate = computeEffectiveDueDate({
    courseWideEndTimeUtcMilli:
      component.pre_lecture_end_time_utc_milli ??
      component.preLectureEndTimeUtcMilli,
    currentUserHash,
    extensions: normalizeTimeExtensions(
      component.pre_lecture_time_extensions ?? component.preLectureTimeExtensions,
    ),
  });
  let nextComponent = component;

  if (
    homeworkEffectiveDueDate.effectiveEndTimeUtcMilli &&
    homeworkEffectiveDueDate.effectiveEndTimeUtcMilli !==
      (component.end_time_utc_milli ?? component.endTimeUtcMilli)
  ) {
    nextComponent = {
      ...nextComponent,
      end_time_utc_milli: homeworkEffectiveDueDate.effectiveEndTimeUtcMilli,
      endTimeUtcMilli: homeworkEffectiveDueDate.effectiveEndTimeUtcMilli,
    };
  }

  if (
    preLectureEffectiveDueDate.effectiveEndTimeUtcMilli &&
    preLectureEffectiveDueDate.effectiveEndTimeUtcMilli !==
      (component.pre_lecture_end_time_utc_milli ??
        component.preLectureEndTimeUtcMilli)
  ) {
    nextComponent = {
      ...nextComponent,
      pre_lecture_end_time_utc_milli:
        preLectureEffectiveDueDate.effectiveEndTimeUtcMilli,
      preLectureEndTimeUtcMilli:
        preLectureEffectiveDueDate.effectiveEndTimeUtcMilli,
    };
  }

  return nextComponent;
}

function normalizeSentimentArray(
  value: unknown,
  { clampNormalized }: { clampNormalized: boolean },
) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeNumber(entry))
    .filter((entry): entry is number => entry !== undefined)
    .map((entry) => {
      if (clampNormalized) {
        return Math.min(Math.max(entry, 0), 1);
      }

      return Math.max(entry, 0);
    });
}

function normalizeIntervalLength(value: unknown): number | undefined {
  const intervalLength = normalizeNumber(value);

  if (intervalLength === undefined || intervalLength <= 0) {
    return undefined;
  }

  return intervalLength;
}

function normalizeVideoSectionStats(
  component: RawComponent,
): VideoSectionStatsMap {
  const statsEntries = toSectionStatsArray(
    component.section_stats ?? component.sectionStats,
  );
  const videoSectionStats: VideoSectionStatsMap = {};

  statsEntries.forEach((sectionStats) => {
    const rawVideoSectionStats =
      sectionStats.video_section_stats ?? sectionStats.videoSectionStats;

    if (!isRecord(rawVideoSectionStats)) {
      return;
    }

    const sectionId =
      normalizeIdentifier(rawVideoSectionStats.id) ??
      normalizeIdentifier(sectionStats.id);

    if (!sectionId) {
      return;
    }

    const videos: VideoSectionStatsMap[string]['videos'] = {};

    toVideoStatsArray(
      rawVideoSectionStats.video_stats ?? rawVideoSectionStats.videoStats,
    ).forEach((videoStats) => {
      const videoId = normalizeIdentifier(videoStats.id);

      if (!videoId) {
        return;
      }

      const normalizedIntervals =
        videoStats.normalized_intervals ?? videoStats.normalizedIntervals;
      const cumulativeIntervals =
        videoStats.cumulative_intervals ?? videoStats.cumulativeIntervals;
      const intervalLength =
        normalizeIntervalLength(
          normalizedIntervals?.interval_length ??
            normalizedIntervals?.intervalLength,
        ) ??
        normalizeIntervalLength(
          cumulativeIntervals?.interval_length ??
            cumulativeIntervals?.intervalLength,
        );

      if (!intervalLength) {
        return;
      }

      videos[videoId] = {
        cumulativeSentiment: normalizeSentimentArray(
          cumulativeIntervals?.sentiment,
          { clampNormalized: false },
        ),
        intervalLength,
        normalizedSentiment: normalizeSentimentArray(
          normalizedIntervals?.sentiment,
          { clampNormalized: true },
        ),
        videoId,
      };
    });

    videoSectionStats[sectionId] = {
      id: sectionId,
      videos,
    };
  });

  return videoSectionStats;
}

function applyVideoSectionStatsMapping(
  videoSectionMap: Record<string, string>,
  videoIdSet: Set<string>,
  videoSectionStats: VideoSectionStatsMap,
) {
  Object.values(videoSectionStats).forEach((sectionStats) => {
    Object.keys(sectionStats.videos).forEach((videoId) => {
      videoIdSet.add(videoId);
      // Sentiment submissions and lookups use the stats section id, not the
      // authored video section id, so stats-derived mappings take precedence.
      videoSectionMap[videoId] = sectionStats.id;
    });
  });
}

function toContentUnitAnnotationGroupArray(value: unknown): RawContentUnitAnnotations[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawContentUnitAnnotations[];
}

function toAnnotationUnitArray(value: unknown): RawAnnotationUnit[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawAnnotationUnit[];
}

function normalizeAnnotationUnit(
  contentUnitId: string,
  annotation: RawAnnotationUnit,
): Annotation | null {
  const id = normalizeIdentifier(annotation.id);

  if (!id) {
    return null;
  }

  const exact = normalizeTextQuoteValue(annotation.text);
  const startOffset = normalizeNumber(annotation.start_offset ?? annotation.startOffset);
  const endOffset = normalizeNumber(annotation.end_offset ?? annotation.endOffset);

  if (!exact && (startOffset === undefined || endOffset === undefined)) {
    return null;
  }

  const note = normalizeTextQuoteValue(annotation.annotation);
  const rawTextColor = annotation.text_color ?? annotation.textColor;

  return {
    contentUnitId,
    id,
    kind: getAnnotationKind(note),
    note,
    quote: {
      endOffset: endOffset ?? null,
      exact,
      prefix: normalizeTextQuoteValue(
        annotation.text_prefix ?? annotation.textPrefix,
      ),
      startOffset: startOffset ?? null,
      suffix: normalizeTextQuoteValue(
        annotation.text_suffix ?? annotation.textSuffix,
      ),
    },
    textColor: isAnnotationTextColor(rawTextColor) ? rawTextColor : undefined,
    timestamp: normalizeNumber(annotation.timestamp) ?? 0,
    visibility: 'private',
  };
}

function normalizeContentUnitAnnotations(
  component: RawComponent,
): NormalizedContentUnitAnnotationGroup[] {
  const groups = toContentUnitAnnotationGroupArray(
    component.content_unit_annotations ?? component.contentUnitAnnotations,
  );

  return groups
    .map((group) => {
      const contentUnitId = normalizeIdentifier(
        group.content_unit_id ?? group.contentUnitId,
      );

      if (!contentUnitId) {
        return null;
      }

      const annotations = toAnnotationUnitArray(
        group.annotation_units ?? group.annotationUnits,
      )
        .map((annotation) => normalizeAnnotationUnit(contentUnitId, annotation))
        .filter((annotation): annotation is Annotation => Boolean(annotation))
        .sort((left, right) => left.timestamp - right.timestamp);

      if (annotations.length === 0) {
        return null;
      }

      return {
        annotations,
        contentUnitId,
      } satisfies NormalizedContentUnitAnnotationGroup;
    })
    .filter(
      (group): group is NormalizedContentUnitAnnotationGroup => group !== null,
    );
}

function toContentUnitArray(value: unknown): RawContentUnit[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawContentUnit[];
}

function toQuestionArray(value: unknown): RawQuestion[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestion[];
}

function getDateMessage(value: unknown): RawDateMessage | null {
  return isRecord(value) ? (value as RawDateMessage) : null;
}

function getTimestampMilliseconds(value: unknown): number | null {
  const timestamp = normalizeString(value);

  if (!timestamp) {
    return null;
  }

  const parsed = Number(timestamp);
  return Number.isFinite(parsed) ? parsed : null;
}

function getDateLabel(date?: RawDateMessage | null): string | null {
  if (!date?.year || !date?.month) {
    return null;
  }

  const safeDay = date.day && date.day > 0 ? date.day : 1;

  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(new Date(Date.UTC(date.year, date.month - 1, safeDay)));
}

function getDateTimeLabel(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function getComponentDateLabel(component: RawComponent): string {
  const startLabel = getDateLabel(
    getDateMessage(component.start_date ?? component.startDate),
  );
  const endLabel = getDateLabel(
    getDateMessage(component.end_date ?? component.endDate),
  );

  if (startLabel && endLabel && startLabel !== endLabel) {
    return `${startLabel} - ${endLabel}`;
  }

  return startLabel ?? endLabel ?? 'Dates TBD';
}

function getComponentDueLabel(component: RawComponent): string | undefined {
  const dueTimestamp = getTimestampMilliseconds(
    component.end_time_utc_milli ?? component.endTimeUtcMilli,
  );

  if (dueTimestamp) {
    return `Due ${getDateTimeLabel(dueTimestamp)}`;
  }

  const endLabel = getDateLabel(
    getDateMessage(component.end_date ?? component.endDate),
  );

  return endLabel ? `Due ${endLabel}` : undefined;
}

function getComponentStatusLabel(
  component: RawComponent,
): ComponentPageData['statusLabel'] {
  const isComplete = normalizeBoolean(component.is_complete ?? component.isComplete);

  if (isComplete) {
    return 'Complete';
  }

  const isLive = normalizeBoolean(component.is_live ?? component.isLive);
  return isLive ? 'Live' : 'Upcoming';
}

function getVideoIds(section: RawSection): string[] {
  return toStringArray(
    section.video_section?.video_ids ??
      section.video_section?.videoIds ??
      section.videoSection?.video_ids ??
      section.videoSection?.videoIds,
  );
}

function getVideoSectionIdentifier(section: RawSection): string | null {
  return (
    normalizeIdentifier(section.video_section?.id) ??
    normalizeIdentifier(section.video_section?.uid) ??
    normalizeIdentifier(section.videoSection?.id) ??
    normalizeIdentifier(section.videoSection?.uid)
  );
}

function getSectionIdentifier(section: RawSection, sectionIndex: number): string {
  return (
    normalizeIdentifier(section.id) ??
    normalizeIdentifier(section.uid) ??
    `section-${sectionIndex + 1}`
  );
}

function getHomeworkQuestionCount(section: RawSection) {
  const questionSection = isRecord(section.question_section ?? section.questionSection)
    ? ((section.question_section ?? section.questionSection) as RawQuestionSection)
    : null;
  const questions = Array.isArray(questionSection?.questions)
    ? questionSection.questions
    : [];

  return questions.filter(isRecord).length;
}

function getPreLectureQuestionCount(section: RawSection) {
  const questionSection = isRecord(
    section.pre_lecture_question_section ?? section.preLectureQuestionSection,
  )
    ? ((
        section.pre_lecture_question_section ?? section.preLectureQuestionSection
      ) as RawQuestionSection)
    : null;
  const questions = Array.isArray(questionSection?.questions)
    ? questionSection.questions
    : [];

  return questions.filter(isRecord).length;
}

function getDisplayNameParts(displayName: string | null | undefined) {
  if (typeof displayName !== 'string') {
    return {};
  }

  const parts = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length === 0) {
    return {};
  }

  return {
    firstName: parts[0],
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : undefined,
  };
}

function normalizeQuestionAssignmentType(
  value: unknown,
): RawQuestionAssignmentType {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return QUESTION_ASSIGNMENT_TYPE_MAP[String(value)] ?? 'ALL';
  }

  const normalized = normalizeString(value)?.toUpperCase();
  return normalized ? (QUESTION_ASSIGNMENT_TYPE_MAP[normalized] ?? 'ALL') : 'ALL';
}

function normalizeQuestionType(value: unknown): RawQuestionType {
  if (typeof value === 'number') {
    return QUESTION_TYPE_MAP[String(value)] ?? 'MULTIPLE_CHOICE';
  }

  const normalized = normalizeString(value)?.toUpperCase();
  return normalized ? (QUESTION_TYPE_MAP[normalized] ?? 'MULTIPLE_CHOICE') : 'MULTIPLE_CHOICE';
}

function getQuestionSettings(
  component: RawComponent,
  kind: 'homework' | 'preLecture',
): RawQuestionSettings | undefined {
  const rawSettings =
    kind === 'preLecture'
      ? component.pre_lecture_question_settings ??
        component.preLectureQuestionSettings
      : component.question_settings ??
        component.questionSettings ??
        component.course_question_settings ??
        component.courseQuestionSettings;

  return isRecord(rawSettings) ? (rawSettings as RawQuestionSettings) : undefined;
}

function getAssignedQuestionIndexes(
  questionCount: number,
  settings: RawQuestionSettings | undefined,
): number[] {
  const assignmentType = normalizeQuestionAssignmentType(settings?.type);

  if (assignmentType !== 'USER_DEFINED') {
    return Array.from({ length: questionCount }, (_unused, index) => index);
  }

  const selectedIndexes = Array.isArray(
    settings?.user_defined_numbers ?? settings?.userDefinedNumbers,
  )
    ? (settings?.user_defined_numbers ?? settings?.userDefinedNumbers)!
        .map((entry) => normalizeNumber(entry))
        .filter((entry): entry is number => entry !== undefined)
        .map((entry) => Math.trunc(entry))
        .filter((entry) => entry >= 0 && entry < questionCount)
    : [];

  return Array.from(new Set(selectedIndexes));
}

function stripHtml(value: string) {
  return value
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getQuestionPreviewText(question: RawQuestion, index: number) {
  const directContent = normalizeString(question.content);

  if (directContent) {
    const preview = stripHtml(directContent);
    if (preview.length > 0) {
      return preview;
    }
  }

  const contentSection = isRecord(question.content_section ?? question.contentSection)
    ? ((question.content_section ?? question.contentSection) as RawContentSection)
    : null;
  const firstTextUnit = toContentUnitArray(
    contentSection?.content_units ?? contentSection?.contentUnits,
  ).find((unit) => normalizeString(unit.text));
  const preview = normalizeString(firstTextUnit?.text);

  if (preview) {
    const normalizedPreview = stripHtml(preview);
    if (normalizedPreview.length > 0) {
      return normalizedPreview;
    }
  }

  return `Question ${index + 1}`;
}

function getQuestionTypeLabel(question: RawQuestion) {
  switch (normalizeQuestionType(question.type)) {
    case 'EXACT_ANSWER':
      return 'Exact answer';
    case 'MULTI_PART':
      return 'Multi-part question';
    case 'NUMERICAL_WITH_ERROR':
      return 'Numerical answer';
    case 'MULTIPLE_CHOICE':
    default:
      return 'Choose one answer';
  }
}

function truncateQuestionPreview(previewText: string, maxLength = 160) {
  if (previewText.length <= maxLength) {
    return previewText;
  }

  return `${previewText.slice(0, maxLength - 1).trimEnd()}…`;
}

function getControlPanelQuestionOptions(
  component: RawComponent,
  kind: 'homework' | 'preLecture',
): ControlPanelQuestionOption[] {
  const sections = toSectionArray(component.sections);
  let globalQuestionIndex = 0;

  return sections.flatMap((section, sectionIndex) => {
    const questionSection =
      kind === 'preLecture'
        ? section.pre_lecture_question_section ?? section.preLectureQuestionSection
        : section.question_section ?? section.questionSection;

    if (!isRecord(questionSection)) {
      return [];
    }

    const sectionId = getSectionIdentifier(section, sectionIndex);

    return toQuestionArray((questionSection as RawQuestionSection).questions).map(
      (question, questionIndexWithinSection) => {
        const optionIndex = globalQuestionIndex;
        globalQuestionIndex += 1;

        return {
          id:
            normalizeIdentifier(question.id) ??
            normalizeIdentifier(question.uid) ??
            `${kind}-${sectionId}-${questionIndexWithinSection + 1}`,
          index: optionIndex,
          previewText: truncateQuestionPreview(
            getQuestionPreviewText(question, optionIndex),
          ),
          questionIndexWithinSection,
          sectionId,
          sectionIndex,
          typeLabel: getQuestionTypeLabel(question),
        };
      },
    );
  });
}

function buildControlPanelData(
  component: RawComponent,
  courseDefaults: CourseQuestionDefaults,
): ControlPanelData {
  const homeworkQuestionOptions = getControlPanelQuestionOptions(component, 'homework');
  const preLectureQuestionOptions = getControlPanelQuestionOptions(
    component,
    'preLecture',
  );
  const questionSettings = getQuestionSettings(component, 'homework');
  const preLectureQuestionSettings = getQuestionSettings(component, 'preLecture');
  const contentStartTimeUtcMilli = normalizeString(
    component.content_start_time_utc_milli ?? component.contentStartTimeUtcMilli,
  );
  const homeworkStartTimeUtcMilli = normalizeString(
    component.start_time_utc_milli ?? component.startTimeUtcMilli,
  );
  const homeworkEndTimeUtcMilli = normalizeString(
    component.end_time_utc_milli ?? component.endTimeUtcMilli,
  );
  const preLectureStartTimeUtcMilli = normalizeString(
    component.pre_lecture_start_time_utc_milli ??
      component.preLectureStartTimeUtcMilli,
  );
  const preLectureEndTimeUtcMilli = normalizeString(
    component.pre_lecture_end_time_utc_milli ??
      component.preLectureEndTimeUtcMilli,
  );
  const homeworkTimeExtensions = normalizeTimeExtensions(
    component.homework_time_extensions ?? component.homeworkTimeExtensions,
  );
  const preLectureTimeExtensions = normalizeTimeExtensions(
    component.pre_lecture_time_extensions ?? component.preLectureTimeExtensions,
  );
  // The chapter's own settings only: an attempts value it lacks comes from the
  // course defaults, which are passed in beside it.
  const chapterHomeworkSettings =
    component.question_settings ?? component.questionSettings;
  const chapterPreLectureSettings =
    component.pre_lecture_question_settings ?? component.preLectureQuestionSettings;

  return {
    attempts: {
      homework: resolveSectionAttempts(
        isRecord(chapterHomeworkSettings) ? chapterHomeworkSettings : null,
        courseDefaults.courseQuestionSettings,
      ),
      preLecture: resolveSectionAttempts(
        isRecord(chapterPreLectureSettings) ? chapterPreLectureSettings : null,
        courseDefaults.coursePrelectureQuestionSettings,
      ),
    },
    ...(contentStartTimeUtcMilli ? { contentStartTimeUtcMilli } : {}),
    hasHomeworkQuestionSection: homeworkQuestionOptions.length > 0,
    hasPreLectureQuestionSection: preLectureQuestionOptions.length > 0,
    homeworkAssignedIndexes: getAssignedQuestionIndexes(
      homeworkQuestionOptions.length,
      questionSettings,
    ),
    ...(homeworkEndTimeUtcMilli ? { homeworkEndTimeUtcMilli } : {}),
    homeworkQuestionOptions,
    ...(homeworkStartTimeUtcMilli ? { homeworkStartTimeUtcMilli } : {}),
    homeworkTimeExtensions,
    preLectureAssignedIndexes: getAssignedQuestionIndexes(
      preLectureQuestionOptions.length,
      preLectureQuestionSettings,
    ),
    ...(preLectureEndTimeUtcMilli ? { preLectureEndTimeUtcMilli } : {}),
    preLectureQuestionOptions,
    ...(preLectureStartTimeUtcMilli ? { preLectureStartTimeUtcMilli } : {}),
    preLectureTimeExtensions,
    ...(preLectureQuestionSettings
      ? { preLectureQuestionSettings }
      : {}),
    ...(questionSettings ? { questionSettings } : {}),
  };
}

function buildInstructorUserForStats(
  user: {
    displayName?: string | null;
    email?: string | null;
  },
  kuduUserId: string,
): RawKuduUser {
  const { firstName, lastName } = getDisplayNameParts(user.displayName);

  return {
    archived_course_ids: [],
    course_ids: [],
    dismissed_notifications: [],
    id: kuduUserId,
    profile: {
      first_name: firstName,
      is_instructor: true,
      last_name: lastName,
      preferred_email: user.email ?? undefined,
    },
  };
}

function getInstructorStatsSections(component: RawComponent): InstructorStatsSectionData[] {
  const sections = toSectionArray(component.sections);
  const sectionStats = toSectionStatsArray(
    component.section_stats ?? component.sectionStats,
  );

  return sections.map((section, sectionIndex) => {
    const sectionId = getSectionIdentifier(section, sectionIndex);
    const matchedSectionStats =
      sectionStats.find((stats) => normalizeIdentifier(stats.id) === sectionId) ??
      sectionStats[sectionIndex] ??
      null;
    const totalScore = normalizeNumber(
      matchedSectionStats?.total_score ?? matchedSectionStats?.totalScore,
    );
    const totalPreLectureScore = normalizeNumber(
      matchedSectionStats?.total_pre_lecture_score ??
        matchedSectionStats?.totalPreLectureScore,
    );
    const homeworkQuestionStats = Array.isArray(
      matchedSectionStats?.question_stats ?? matchedSectionStats?.questionStats,
    )
      ? (matchedSectionStats?.question_stats ??
          matchedSectionStats?.questionStats) as RawQuestionStats[]
      : [];
    const preLectureQuestionStats = Array.isArray(
      matchedSectionStats?.pre_lecture_question_stats ??
        matchedSectionStats?.preLectureQuestionStats,
    )
      ? ((matchedSectionStats?.pre_lecture_question_stats ??
          matchedSectionStats?.preLectureQuestionStats) as RawQuestionStats[])
      : [];

    return {
      hasAggregateStats:
        totalScore !== undefined ||
        totalPreLectureScore !== undefined ||
        homeworkQuestionStats.length > 0 ||
        preLectureQuestionStats.length > 0,
      homeworkQuestionCount: getHomeworkQuestionCount(section),
      preLectureQuestionCount: getPreLectureQuestionCount(section),
      sectionId,
      sectionStatsId: normalizeIdentifier(matchedSectionStats?.id) ?? undefined,
      totalPreLectureScore,
      totalScore,
    } satisfies InstructorStatsSectionData;
  });
}

function getInstructorQuestionScores(component: RawComponent): number[] {
  const sectionStats = toSectionStatsArray(
    component.section_stats ?? component.sectionStats,
  );

  return sectionStats.flatMap((stats) => {
    const questionStats = Array.isArray(
      stats.question_stats ?? stats.questionStats,
    )
      ? ((stats.question_stats ?? stats.questionStats) as RawQuestionStats[])
      : [];

    return questionStats.map((questionStat) => normalizeNumber(questionStat.score) ?? 0);
  });
}

function normalizeContentUnitType(value: unknown): RawContentUnitType | null {
  if (typeof value === 'number') {
    return CONTENT_UNIT_TYPE_MAP[String(value)] ?? null;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return null;
  }

  return CONTENT_UNIT_TYPE_MAP[normalized.toUpperCase()] ?? null;
}

function getHeadingText(unit: RawContentUnit): string | undefined {
  return normalizeString(unit.heading) ?? undefined;
}

function getCaptionText(unit: RawContentUnit): string | undefined {
  // The legacy `[alt: ...]` marker is a description, not caption text, so it is
  // stripped from what students see. Applied to every unit type, not just
  // images: a marker anywhere is a marker, and it should never reach the page.
  const displayCaption = extractAltFromCaption(
    normalizeString(unit.caption) ?? undefined,
  ).displayCaption;

  return displayCaption.length > 0 ? displayCaption : undefined;
}

function getImageSize(unit: RawContentUnit): ImageUnitData['size'] {
  const sizeValue = unit.image_size ?? unit.imageSize;

  if (typeof sizeValue === 'number') {
    return sizeValue === 1 ? 'small' : sizeValue === 3 ? 'large' : 'medium';
  }

  const normalized = normalizeString(sizeValue)?.toUpperCase();

  if (
    normalized === '0' ||
    normalized === '2' ||
    normalized === 'MEDIUM' ||
    normalized === 'DEFAULT'
  ) {
    return 'medium';
  }

  if (normalized === 'SMALL') {
    return 'small';
  }

  if (normalized === 'LARGE') {
    return 'large';
  }

  if (normalized === '1') {
    return 'small';
  }

  if (normalized === '3') {
    return 'large';
  }

  return normalizeBoolean(unit.use_small_image ?? unit.useSmallImage)
    ? 'small'
    : 'medium';
}

function getSummaryLabel(
  heading: string | undefined,
  numberedHeading: string | undefined,
  unitPosition: number,
): string {
  return numberedHeading ?? heading ?? `Content item ${unitPosition}`;
}

function getImageAlt(unit: RawContentUnit): string {
  // Resolution order lives in imageAltText: stored alt text, then a legacy
  // caption marker, then empty. The RAW caption is passed because the marker
  // has already been stripped from the display caption.
  return resolveImageAltText({
    altText: normalizeString(unit.alt_text ?? unit.altText) ?? undefined,
    caption: normalizeString(unit.caption) ?? undefined,
  });
}

// Proto enums arrive as either the numeric value or the symbolic name depending
// on the serialization path, so both spellings map here.
function getTableStyle(value: unknown): TableUnitStyle {
  const normalized =
    typeof value === 'number' ? String(value) : normalizeString(value)?.toUpperCase();

  if (normalized === '1' || normalized === 'STRIPED') {
    return 'striped';
  }

  if (normalized === '2' || normalized === 'BORDERED') {
    return 'bordered';
  }

  return 'plain';
}

function getTableDensity(value: unknown): TableUnitDensity {
  const normalized =
    typeof value === 'number' ? String(value) : normalizeString(value)?.toUpperCase();

  return normalized === '1' || normalized === 'COMPACT' ? 'compact' : 'comfortable';
}

function getWidthPercent(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.min(100, Math.max(10, value));
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return 100;
  }

  const numeric = Number(normalized.replace('%', ''));

  if (!Number.isFinite(numeric)) {
    return 100;
  }

  return Math.min(100, Math.max(10, numeric));
}

function getHeightPixels(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return 420;
  }

  const numeric = Number(normalized);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 420;
}

function createBaseUnit(
  unit: RawContentUnit,
  unitPosition: number,
  nextHeadingNumber: () => number,
) {
  const heading = getHeadingText(unit);
  const caption = getCaptionText(unit);
  const collapseByDefault =
    normalizeBoolean(unit.collapse_by_default ?? unit.collapseByDefault) ??
    false;
  const disableAutoNumbering =
    normalizeBoolean(
      unit.disable_auto_numbering ?? unit.disableAutoNumbering,
    ) ?? false;
  const headingDisplay = getContentHeadingDisplay({
    collapseByDefault,
    disableAutoNumbering,
    heading,
  });

  const unitId = normalizeString(unit.id) ?? `content-unit-${unitPosition}`;
  let numberedHeading: string | undefined;

  if (heading) {
    numberedHeading = headingDisplay.shouldNumber
      ? `${nextHeadingNumber()}. ${heading}`
      : heading;
  }

  return {
    caption,
    collapseByDefault,
    disableAutoNumbering,
    heading,
    anchorId:
      heading && !collapseByDefault ? getContentUnitAnchorId(unitId) : undefined,
    id: unitId,
    numberedHeading,
    summaryLabel: getSummaryLabel(heading, numberedHeading, unitPosition),
  };
}

function isExampleHeading(heading: string | undefined) {
  if (!heading) {
    return false;
  }

  return /^example\b/i.test(heading.trim());
}

function getContentHeadingDisplay({
  collapseByDefault,
  disableAutoNumbering,
  heading,
}: {
  collapseByDefault: boolean;
  disableAutoNumbering: boolean;
  heading: string | undefined;
}): ContentHeadingDisplay {
  if (!heading || collapseByDefault) {
    return {
      includeInToc: false,
      level: 'normal',
      shouldNumber: false,
    };
  }

  if (isExampleHeading(heading)) {
    return {
      includeInToc: true,
      level: 'example',
      shouldNumber: false,
    };
  }

  if (disableAutoNumbering) {
    return {
      includeInToc: true,
      level: 'normal',
      shouldNumber: false,
    };
  }

  return {
    includeInToc: true,
    level: 'normal',
    shouldNumber: true,
  };
}

function createTocEntries(
  sections: NormalizedSection[],
  videoIds: string[],
  questionCount: number,
): TocEntry[] {
  const tocEntries: TocEntry[] = [];
  let contentHeadingNumber = 0;
  const hasVideos = videoIds.length > 0;

  if (hasVideos) {
    tocEntries.push({
      id: VIDEO_SECTION_ID,
      label: 'Video',
      level: 'normal',
      type: 'video',
    });
  }

  sections.forEach((section) => {
    section.units.forEach((unit) => {
      const headingDisplay = getContentHeadingDisplay({
        collapseByDefault: unit.collapseByDefault,
        disableAutoNumbering: unit.disableAutoNumbering,
        heading: unit.heading,
      });

      if (!unit.heading || !unit.anchorId || !headingDisplay.includeInToc) {
        return;
      }

      if (!headingDisplay.shouldNumber) {
        tocEntries.push({
          id: unit.anchorId,
          label: unit.heading,
          level: headingDisplay.level,
          type: 'content',
        });
        return;
      }

      contentHeadingNumber += 1;
      tocEntries.push({
        id: unit.anchorId,
        label: unit.heading,
        level: 'normal',
        numberedLabel: `${contentHeadingNumber}. ${unit.heading}`,
        type: 'content',
      });
    });
  });

  if (questionCount > 0) {
    tocEntries.push({
      id: QUESTION_SECTION_ID,
      label: 'Questions',
      level: 'normal',
      type: 'question',
    });
  }

  return tocEntries;
}

/**
 * Normalizes one content unit.
 *
 * Exported so the question normalizer can delegate to it rather than keeping a
 * parallel implementation. It had one, and it did not know about tables — a
 * TABLE unit in a question prompt fell through to the video branch and was
 * silently dropped. Every unit type must work the same in both places, so there
 * is exactly one place that knows the types.
 */
export function normalizeContentUnit(
  unit: RawContentUnit,
  unitPosition: number,
  nextHeadingNumber: () => number,
): NormalizedContentUnit | null {
  const kind = normalizeContentUnitType(unit.type);

  if (!kind) {
    console.warn('Skipping unsupported content unit type', {
      id: unit.id,
      type: unit.type,
    });
    return null;
  }

  const baseUnit = createBaseUnit(unit, unitPosition, nextHeadingNumber);

  if (kind === 'TEXT') {
    const html = unit.text ?? '';

    if (typeof html !== 'string') {
      return null;
    }

    return {
      ...baseUnit,
      html,
      kind: 'text',
    };
  }

  if (kind === 'IMAGE') {
    const src = resolveImageUnitSrc(unit);

    if (!src) {
      console.warn('Skipping image content unit with no source', {
        id: unit.id,
      });
      return null;
    }

    const resizableSource = getResizableImageSource(unit);

    return {
      ...baseUnit,
      alt: getImageAlt(unit),
      kind: 'image',
      ...(resizableSource ? { resizableSource } : {}),
      size: getImageSize(unit),
      src,
    };
  }

  if (kind === 'TABLE') {
    const tableUnit = isRecord(unit.table_unit ?? unit.tableUnit)
      ? ((unit.table_unit ?? unit.tableUnit) as RawTableUnit)
      : null;
    const html = typeof tableUnit?.html === 'string' ? tableUnit.html : '';

    if (html.trim().length === 0) {
      console.warn('Skipping table content unit with no markup', { id: unit.id });
      return null;
    }

    return {
      ...baseUnit,
      density: getTableDensity(tableUnit?.density),
      // Defaults ON when absent: proto2 omits a false bool from the wire, but a
      // table authored before this field existed still means its first row to be
      // a header. `?? true` keeps those tables reading correctly.
      headerRow:
        normalizeBoolean(tableUnit?.header_row ?? tableUnit?.headerRow) ?? true,
      html,
      kind: 'table',
      stickyHeader:
        normalizeBoolean(tableUnit?.sticky_header ?? tableUnit?.stickyHeader) ??
        false,
      style: getTableStyle(tableUnit?.style),
    };
  }

  if (kind === 'IFRAME') {
    const iframeUnit = isRecord(unit.iframe_unit ?? unit.iframeUnit)
      ? ((unit.iframe_unit ?? unit.iframeUnit) as RawIframeUnit)
      : null;
    const src = normalizeString(iframeUnit?.source);

    if (!src) {
      console.warn('Skipping iframe content unit with no source', {
        id: unit.id,
      });
      return null;
    }

    // A source on the configured applet origin is a Kudu applet: route it to
    // the sandboxed AppletFrame host (postMessage handshake, no
    // allow-same-origin) instead of the generic iframe renderer. Any other
    // source stays a plain iframe. No backend/proto change — the applet
    // reference rides in the persisted `iframe_unit.source` URL.
    const applet = parseAppletUrl(src);
    if (applet) {
      return {
        ...baseUnit,
        appletId: applet.appletId,
        config: applet.config,
        kind: 'applet',
        title: baseUnit.heading ?? baseUnit.caption ?? 'Interactive applet',
        version: applet.version,
      };
    }

    // A JupyterLite content-site URL on the applet origin: a path-based applet
    // rendered in a storage-capable sandbox at a tall fixed height.
    const jupyterlite = parseJupyterliteUrl(src);
    if (jupyterlite) {
      return {
        ...baseUnit,
        appletId: jupyterlite.appletId,
        config: {},
        kind: 'applet',
        title: baseUnit.heading ?? baseUnit.caption ?? 'Interactive notebook',
        version: '',
        srcOverride: jupyterlite.srcOverride,
        sandbox: jupyterlite.sandbox,
        fixedHeight: jupyterlite.fixedHeight,
        resettable: jupyterlite.resettable,
        resetLabel: jupyterlite.resetLabel,
      };
    }

    return {
      ...baseUnit,
      height: getHeightPixels(iframeUnit?.height),
      kind: 'iframe',
      src,
      title: baseUnit.heading ?? baseUnit.caption ?? 'Embedded activity',
      widthPercent: getWidthPercent(iframeUnit?.width),
    };
  }

  const videoId = normalizeString(unit.video_id ?? unit.videoId);

  if (!videoId) {
    console.warn('Skipping inline video content unit with no video ID', {
      id: unit.id,
    });
    return null;
  }

  return {
    ...baseUnit,
    embedUrl: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}`,
    kind: 'inlineVideo',
    title: baseUnit.heading ?? baseUnit.caption ?? 'Embedded video',
    videoId,
  };
}

export function unwrapStudentComponentResponse(
  response:
    | RawGetCourseStudentComponentPayload
    | LegacyKuduResponseEnvelope<RawGetCourseStudentComponentPayload>,
): RawComponent | null {
  return unwrapStudentComponentPayload(response)?.component ?? null;
}

function unwrapStudentComponentPayload(
  response:
    | RawGetCourseStudentComponentPayload
    | LegacyKuduResponseEnvelope<RawGetCourseStudentComponentPayload>,
): RawGetCourseStudentComponentPayload | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapStudentComponentPayload(
      response.Response as RawGetCourseStudentComponentPayload,
    );
  }

  if (isRecord(response)) {
    return response as RawGetCourseStudentComponentPayload;
  }

  return null;
}

function unwrapBaseComponentResponse(
  response:
    | RawGetComponentPayload
    | LegacyKuduResponseEnvelope<RawGetComponentPayload>,
): RawComponent | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapBaseComponentResponse(
      response.Response as RawGetComponentPayload,
    );
  }

  if (isRecord(response) && isRecord(response.component)) {
    return response.component as RawComponent;
  }

  return null;
}

function unwrapSectionResponse(
  response:
    | RawGetSectionPayload
    | LegacyKuduResponseEnvelope<RawGetSectionPayload>,
): RawSection | null {
  if (isRecord(response) && 'Response' in response) {
    return unwrapSectionResponse(
      response.Response as RawGetSectionPayload,
    );
  }

  if (isRecord(response) && isRecord(response.section)) {
    return response.section as RawSection;
  }

  return null;
}

function getComponentSections(component: RawComponent | null | undefined): RawSection[] {
  return toSectionArray(component?.sections);
}

function getComponentSectionIds(
  component: RawComponent | null | undefined,
): string[] {
  return toStringArray(component?.section_ids ?? component?.sectionIds);
}

function getMergedContentSection(
  section: RawSection | undefined,
): RawContentSection | null {
  const contentSection = section?.content_section ?? section?.contentSection;
  return isRecord(contentSection) ? (contentSection as RawContentSection) : null;
}

function getMergedVideoSection(
  section: RawSection | undefined,
): RawVideoSection | null {
  const videoSection = section?.video_section ?? section?.videoSection;
  return isRecord(videoSection) ? (videoSection as RawVideoSection) : null;
}

function getMergedQuestionSection(
  section: RawSection | undefined,
  kind: 'homework' | 'preLecture',
): RawQuestionSection | null {
  const questionSection =
    kind === 'homework'
      ? section?.question_section ?? section?.questionSection
      : section?.pre_lecture_question_section ?? section?.preLectureQuestionSection;

  return isRecord(questionSection) ? (questionSection as RawQuestionSection) : null;
}

// Which questions a section has, and what each of them says, is the chapter's
// alone to decide: the same rule as for its content, video and coach sections
// below.
//
// A record can hold a copy of every question. A staff member's does: it is a
// snapshot of the whole chapter, written when the class statistics are
// refreshed and when the chapter closes at its due time. The copy goes stale
// the moment the chapter is saved, because a question saved by someone other
// than its author is stored under a new uid.
//
// This used to lay the record's questions over the chapter's and keep any that
// matched none of them as further questions ("a genuinely student-only
// question"). No record owns a question. After one save from the editor none
// of the 61 copies in an instructor's record matched, the page listed 122
// questions, and the editor and the control panel save what the page holds:
// 122, then 183, then 244 (October 2026). Twice before, a narrower version of
// the same thing was fixed by matching more carefully (a uid on one side and
// an id on the other; parts with no identifier). What does not match is not a
// new question, however it is matched.
//
// So a section the chapter supplies takes both of its question sections from
// the chapter, as they are: no question is added, none is brought back after
// the chapter dropped it, and none is given an id the chapter did not give it.
// A viewer's own answers are not on questions at all; they are in the record's
// section_stats, merged at the component level. Only a section the chapter
// does not supply (the chapter could not be read) falls back to the record's.
function setAuthoredQuestionSection(
  section: RawSection,
  key: 'pre_lecture_question_section' | 'question_section',
  camelCaseKey: 'preLectureQuestionSection' | 'questionSection',
  questionSection: RawQuestionSection | null,
) {
  const target = section as Record<string, unknown>;

  // Never both spellings: a reader takes the first it finds.
  delete target[camelCaseKey];

  if (questionSection) {
    target[key] = questionSection;
  } else {
    delete target[key];
  }
}

function getRawVideoSectionStats(
  sectionStats: RawSectionStats,
): RawVideoSectionStats | null {
  const rawVideoSectionStats =
    sectionStats.video_section_stats ?? sectionStats.videoSectionStats;

  return isRecord(rawVideoSectionStats)
    ? (rawVideoSectionStats as RawVideoSectionStats)
    : null;
}

function mergeSectionStatsWithVideoStats(
  primaryStatsValue: unknown,
  fallbackStatsValue: unknown,
) {
  const primaryStats = toSectionStatsArray(primaryStatsValue);
  const fallbackStats = toSectionStatsArray(fallbackStatsValue);

  if (fallbackStats.length === 0) {
    return primaryStats.length > 0 ? primaryStats : undefined;
  }

  if (primaryStats.length === 0) {
    return fallbackStats;
  }

  const fallbackStatsById = new Map<string, RawSectionStats>();
  fallbackStats.forEach((sectionStats) => {
    const sectionStatsId = normalizeIdentifier(sectionStats.id);

    if (sectionStatsId) {
      fallbackStatsById.set(sectionStatsId, sectionStats);
    }
  });

  const mergedStats = primaryStats.map((sectionStats, index) => {
    if (getRawVideoSectionStats(sectionStats)) {
      return sectionStats;
    }

    const sectionStatsId = normalizeIdentifier(sectionStats.id);
    const fallbackSectionStats =
      (sectionStatsId ? fallbackStatsById.get(sectionStatsId) : undefined) ??
      fallbackStats[index];
    const fallbackVideoSectionStats = fallbackSectionStats
      ? getRawVideoSectionStats(fallbackSectionStats)
      : null;

    if (!fallbackVideoSectionStats) {
      return sectionStats;
    }

    return {
      ...sectionStats,
      video_section_stats: fallbackVideoSectionStats,
    };
  });

  fallbackStats.forEach((sectionStats, index) => {
    const fallbackVideoSectionStats = getRawVideoSectionStats(sectionStats);

    if (!fallbackVideoSectionStats) {
      return;
    }

    const sectionStatsId = normalizeIdentifier(sectionStats.id);
    const hasPrimaryMatch = sectionStatsId
      ? primaryStats.some(
          (primarySectionStats) =>
            normalizeIdentifier(primarySectionStats.id) === sectionStatsId,
        )
      : index < primaryStats.length;

    if (!hasPrimaryMatch) {
      mergedStats.push(sectionStats);
    }
  });

  return mergedStats;
}

// Exported for unit testing the authored-metadata overlay (see useComponent.test).
export function mergeComponentMetadata(
  studentComponent: RawComponent,
  lessonComponent: RawComponent | null,
  includeLessonVideoStats = false,
): RawComponent {
  if (!lessonComponent) {
    return studentComponent;
  }

  const mergedComponent: RawComponent = {
    ...studentComponent,
  };
  const authoredKeys: Array<keyof RawComponent> = [
    'content_start_time_utc_milli',
    'contentStartTimeUtcMilli',
    'course_question_settings',
    'courseQuestionSettings',
    'end_date',
    'end_time_utc_milli',
    'endDate',
    'endTimeUtcMilli',
    'homework_time_extensions',
    'homeworkTimeExtensions',
    'id',
    // The authored visibility AND completion flags are instructor-owned schedule
    // metadata and must override the student copy. LEGACY SUPPORT: components
    // closed with the old "close Now" action set is_live=false + is_complete=true
    // WITHOUT an end_time_utc_milli. Overlaying is_complete (not just is_live) is
    // what lets the student's merged component read as "closed" so the section's
    // questions/content stay visible. The current UI writes an end timestamp when
    // closing, so this only matters for those legacy, flag-only records.
    'is_complete',
    'is_live',
    'is_pre_lecture_complete',
    'is_pre_lecture_live',
    'is_section_divider',
    'isComplete',
    'isLive',
    'isPreLectureComplete',
    'isPreLectureLive',
    'isSectionDivider',
    'pre_lecture_end_time_utc_milli',
    'pre_lecture_question_settings',
    'pre_lecture_start_time_utc_milli',
    'pre_lecture_time_extensions',
    'preLectureEndTimeUtcMilli',
    'preLectureQuestionSettings',
    'preLectureStartTimeUtcMilli',
    'preLectureTimeExtensions',
    'question_settings',
    'questionSettings',
    'section_ids',
    'sectionIds',
    'start_date',
    'start_time_utc_milli',
    'startDate',
    'startTimeUtcMilli',
    'title',
    'uid',
  ];

  authoredKeys.forEach((key) => {
    if (key in lessonComponent) {
      (mergedComponent as Record<string, unknown>)[key] =
        (lessonComponent as Record<string, unknown>)[key];
    }
  });

  if (includeLessonVideoStats) {
    const mergedSectionStats = mergeSectionStatsWithVideoStats(
      mergedComponent.section_stats ?? mergedComponent.sectionStats,
      lessonComponent.section_stats ?? lessonComponent.sectionStats,
    );

    if (mergedSectionStats) {
      mergedComponent.section_stats = mergedSectionStats;
    }
  }

  return mergedComponent;
}

export function mergeSectionContent(
  studentSection: RawSection | undefined,
  lessonSection: RawSection | undefined,
): RawSection | null {
  if (!studentSection && !lessonSection) {
    return null;
  }

  const mergedSection: RawSection = {
    ...(lessonSection ?? {}),
    ...(studentSection ?? {}),
  };
  const lessonContentSection = getMergedContentSection(lessonSection);
  const studentContentSection = getMergedContentSection(studentSection);
  const lessonVideoSection = getMergedVideoSection(lessonSection);
  const studentVideoSection = getMergedVideoSection(studentSection);
  const studentHomeworkSection = studentSection
    ? getMergedQuestionSection(studentSection, 'homework')
    : null;
  const lessonHomeworkSection = lessonSection
    ? getMergedQuestionSection(lessonSection, 'homework')
    : null;
  const studentPreLectureSection = studentSection
    ? getMergedQuestionSection(studentSection, 'preLecture')
    : null;
  const lessonPreLectureSection = lessonSection
    ? getMergedQuestionSection(lessonSection, 'preLecture')
    : null;

  if (lessonContentSection || studentContentSection) {
    mergedSection.content_section = lessonContentSection ?? studentContentSection;
  }

  if (lessonVideoSection || studentVideoSection) {
    mergedSection.video_section = lessonVideoSection ?? studentVideoSection;
  }

  setAuthoredQuestionSection(
    mergedSection,
    'question_section',
    'questionSection',
    lessonSection ? lessonHomeworkSection : studentHomeworkSection,
  );
  setAuthoredQuestionSection(
    mergedSection,
    'pre_lecture_question_section',
    'preLectureQuestionSection',
    lessonSection ? lessonPreLectureSection : studentPreLectureSection,
  );

  // The Kai Coach section is authored content (like content_section) — its
  // sessions/activities come from the lesson, not per-user data. Always take the
  // lesson (fresh) definition so a stale per-user snapshot cannot shadow it. This
  // matters for instructors: an earlier "Update Stats" writes an instructor
  // CourseStudentComponent that snapshots the coach section; without this, the
  // object-spread above (student overrides lesson) would surface that stale copy
  // and hide sessions added since. Per-user progress lives in section_stats, which
  // is merged at the component level and is unaffected here.
  const lessonCoachSection =
    lessonSection?.kai_coach_section ?? lessonSection?.kaiCoachSection;
  const studentCoachSection =
    studentSection?.kai_coach_section ?? studentSection?.kaiCoachSection;
  if (lessonCoachSection || studentCoachSection) {
    mergedSection.kai_coach_section = lessonCoachSection ?? studentCoachSection;
    // Avoid leaving both spellings on the merged section.
    delete mergedSection.kaiCoachSection;
  }

  return mergedSection;
}

function mergeComponentSections(
  studentSections: RawSection[],
  lessonSections: RawSection[],
  sectionIds: string[],
): RawSection[] {
  if (lessonSections.length === 0) {
    return studentSections;
  }

  if (studentSections.length === 0) {
    return lessonSections;
  }

  const studentSectionsById = new Map<string, RawSection>();
  const lessonSectionsById = new Map<string, RawSection>();
  const orderedIds: string[] = [];
  const seenIds = new Set<string>();
  const appendId = (id: string) => {
    if (seenIds.has(id)) {
      return;
    }

    seenIds.add(id);
    orderedIds.push(id);
  };

  studentSections.forEach((section, index) => {
    studentSectionsById.set(getSectionIdentifier(section, index), section);
  });
  lessonSections.forEach((section, index) => {
    lessonSectionsById.set(getSectionIdentifier(section, index), section);
  });

  sectionIds.forEach((sectionId) => appendId(sectionId));
  lessonSections.forEach((section, index) => {
    appendId(getSectionIdentifier(section, index));
  });
  studentSections.forEach((section, index) => {
    appendId(getSectionIdentifier(section, index));
  });

  return orderedIds
    .map((sectionId) =>
      mergeSectionContent(
        studentSectionsById.get(sectionId),
        lessonSectionsById.get(sectionId),
      ),
    )
    .filter((section): section is RawSection => section !== null);
}

/**
 * Reads the chapter: its content, its questions and its schedule. A student's
 * page carries on without it, from their record; a staff page fails.
 */
async function readChapter(
  client: ReturnType<typeof createApiClient>,
  courseId: string,
  contentId: string,
  isInstructorRequest: boolean,
): Promise<RawComponent | null> {
  let baseComponent: RawComponent | null = null;

  try {
    const componentResponse = await client.invokeKuduMethod<
      RawGetComponentPayload | LegacyKuduResponseEnvelope<RawGetComponentPayload>,
      { component_id: string; course_id: string }
    >('GetComponent', {
      component_id: contentId,
      course_id: courseId,
    });

    baseComponent = unwrapBaseComponentResponse(componentResponse);
  } catch (error) {
    console.error('Failed to hydrate content page component via GetComponent', {
      contentId,
      courseId,
      error,
    });

    // Staff can save what this page holds (the editor, the control panel, the
    // copy and delete actions all send it back as the chapter). Without the
    // chapter all there is to show them is their record, which is not the
    // chapter: at best an old snapshot of it, and since records stopped
    // carrying questions, one with none. Saved, that replaces the chapter for
    // the whole class. So for staff a chapter that could not be read is an
    // error to retry, never a page to work from. Students cannot save a
    // chapter and keep the page they had.
    if (isInstructorRequest) {
      throw error;
    }
  }

  if (isInstructorRequest && !baseComponent) {
    throw createDataError(
      'This chapter could not be loaded. Reload the page to try again.',
    );
  }

  return baseComponent;
}

async function hydrateComponentSections(
  client: ReturnType<typeof createApiClient>,
  component: RawComponent,
  baseComponent: RawComponent | null,
  courseId: string,
  contentId: string,
  isInstructorRequest = false,
): Promise<RawComponent> {
  const includeLessonVideoStats = isInstructorRequest;
  const studentSections = getComponentSections(component);
  let hydratedSections = getComponentSections(baseComponent);
  const sectionIds =
    getComponentSectionIds(component).length > 0
      ? getComponentSectionIds(component)
      : getComponentSectionIds(baseComponent);

  if (hydratedSections.length === 0 && sectionIds.length > 0) {
    hydratedSections = (
      await Promise.all(
        sectionIds.map(async (sectionId) => {
          try {
            const sectionResponse = await client.invokeKuduMethod<
              RawGetSectionPayload | LegacyKuduResponseEnvelope<RawGetSectionPayload>,
              { section_id: string }
            >('GetSection', {
              section_id: sectionId,
            });

            return unwrapSectionResponse(sectionResponse);
          } catch (error) {
            console.error('Failed to hydrate content page section', {
              contentId,
              courseId,
              error,
              sectionId,
            });
            return null;
          }
        }),
      )
    ).filter((section): section is RawSection => section !== null);
  }

  if (hydratedSections.length === 0) {
    return {
      ...mergeComponentMetadata(
        component,
        baseComponent,
        includeLessonVideoStats,
      ),
      section_ids:
        sectionIds.length > 0
          ? sectionIds
          : baseComponent?.section_ids ?? component.section_ids,
      sectionIds:
        sectionIds.length > 0
          ? sectionIds
          : baseComponent?.sectionIds ?? component.sectionIds,
    };
  }

  const mergedSections = mergeComponentSections(
    studentSections,
    hydratedSections,
    sectionIds,
  );

  return {
    ...mergeComponentMetadata(component, baseComponent, includeLessonVideoStats),
    section_ids:
      sectionIds.length > 0
        ? sectionIds
        : baseComponent?.section_ids ?? component.section_ids,
    sectionIds:
      sectionIds.length > 0
        ? sectionIds
      : baseComponent?.sectionIds ?? component.sectionIds,
    sections: mergedSections,
  };
}

export function normalizeComponentResponse(
  component: RawComponent,
  {
    contentId,
    coursePrelectureQuestionSettings,
    courseQuestionSettings,
    instructorUser,
    isInstructorRequest = false,
    kuduUserId,
  }: NormalizeOptions,
): ComponentPageData {
  const videoIdSet = new Set<string>();
  const videoSectionMap: Record<string, string> = {};
  let unitPosition = 0;
  let headingNumber = 0;
  const courseDefaults: CourseQuestionDefaults = {
    coursePrelectureQuestionSettings,
    courseQuestionSettings,
  };
  const timingState = getComponentTimingState(component, undefined, courseDefaults);
  const instructorStatsSections = getInstructorStatsSections(component);

  const sections = toSectionArray(component.sections)
    .map((section, sectionIndex) => {
      const sectionVideoIds = getVideoIds(section);
      const videoSectionId = getVideoSectionIdentifier(section);

      sectionVideoIds.forEach((videoId) => {
        if (!videoIdSet.has(videoId)) {
          videoIdSet.add(videoId);
        }

        if (videoSectionId && !(videoId in videoSectionMap)) {
          videoSectionMap[videoId] = videoSectionId;
        }
      });

      const contentUnits = toContentUnitArray(
        section.content_section?.content_units ??
          section.content_section?.contentUnits ??
          section.contentSection?.content_units ??
          section.contentSection?.contentUnits,
      );

      const normalizedUnits = contentUnits
        .map((unit) => {
          unitPosition += 1;
          return normalizeContentUnit(unit, unitPosition, () => {
            headingNumber += 1;
            return headingNumber;
          });
        })
        .filter((unit): unit is NormalizedContentUnit => unit !== null);

      if (normalizedUnits.length === 0) {
        return null;
      }

      return {
        id:
          normalizeIdentifier(section.id) ??
          normalizeIdentifier(section.uid) ??
          `section-${sectionIndex + 1}`,
        units: normalizedUnits,
      } satisfies NormalizedSection;
    })
    .filter((section): section is NormalizedSection => section !== null);
  const questionSections = normalizeQuestionSections(component, {
    coursePrelectureQuestionSettings,
    courseQuestionSettings,
    isInstructorRequest,
    kuduUserId,
    timingState,
  });
  const questionCount = questionSections.reduce(
    (total, section) => total + section.questionCount,
    0,
  );
  const videoSectionStats = normalizeVideoSectionStats(component);
  applyVideoSectionStatsMapping(videoSectionMap, videoIdSet, videoSectionStats);
  const videoIds = [...videoIdSet];
  const tocEntries = createTocEntries(sections, videoIds, questionCount);
  const classAverageScore = instructorStatsSections.reduce(
    (total, section) => total + (section.totalScore ?? 0),
    0,
  );
  const classAveragePreLectureScore = instructorStatsSections.reduce(
    (total, section) => total + (section.totalPreLectureScore ?? 0),
    0,
  );
  const assignedHomeworkQuestionCount = questionSections.reduce(
    (total, section) =>
      total +
      (section.kind === 'homework'
        ? section.questions.filter((question) => question.isAssigned).length
        : 0),
    0,
  );
  const rawInstructorQuestionScores = isInstructorRequest
    ? getInstructorQuestionScores(component)
    : [];
  const classQuestionScores = Array.from(
    { length: assignedHomeworkQuestionCount },
    (_unused, index) => rawInstructorQuestionScores[index] ?? 0,
  );
  const hasInstructorAggregateStats = instructorStatsSections.some(
    (section) => section.hasAggregateStats,
  );
  const contentUnitAnnotations = normalizeContentUnitAnnotations(component);

  return {
    classAveragePreLectureScore,
    classAverageScore,
    classQuestionScores,
    contentUnitAnnotations,
    controlPanel: buildControlPanelData(component, courseDefaults),
    courseDefaults,
    dateLabel: getComponentDateLabel(component),
    dueLabel: getComponentDueLabel(component),
    hasInstructorAggregateStats,
    id:
      normalizeIdentifier(component.id) ??
      normalizeIdentifier(component.uid) ??
      contentId,
    instructorStatsSections,
    ...(instructorUser ? { instructorUser } : {}),
    isInstructorRequest,
    questionCount,
    questionSections,
    rawComponent: component,
    scoreSummary: getScoreSummary(component),
    sections,
    statusLabel: getComponentStatusLabel(component),
    timingState,
    tocEntries,
    title: normalizeString(component.title) ?? 'Untitled content',
    videoIds,
    videoSectionMap,
    videoSectionStats,
  };
}

// After a student's read finds the close pending, the page reads again this
// long after it: 10 seconds, then 20, then 40, and no more (issue #103).
const CLOSE_PENDING_READ_DELAYS_MS = [10_000, 20_000, 40_000];

/**
 * Whether a student's page read the chapter before its close had finished:
 * their homework shows closed (their own due date, an extension included, has
 * passed; an extension only ever moves it later), the chapter has a due date
 * of its own, which is what the server closes it by, and it is not marked
 * complete. The close has not run, is still running or failed; or the service
 * worker, after its 5-second wait, answered from its cache while it ran. The
 * record read with it is from before the close's grading.
 */
function isChapterClosePending(
  chapter: RawComponent | null,
  timingState: ComponentTimingState,
) {
  return (
    chapter !== null &&
    timingState.isPastDue &&
    getComponentEndTimeUtcMilli(chapter) !== undefined &&
    normalizeBoolean(chapter.is_complete ?? chapter.isComplete) !== true
  );
}

export function useComponent(
  courseId?: string,
  contentId?: string,
  isInstructorRequest = false,
  options?: UseComponentOptions,
): UseQueryResult<ComponentPageData, ApiError> {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, loading, user } = useAuth();
  const questionSettingsCacheKey = getQuestionSettingsCacheKey(options);

  return useQuery<ComponentPageData, ApiError>({
    enabled: !loading && Boolean(user) && Boolean(courseId) && Boolean(contentId),
    queryFn: async ({ queryKey }) => {
      if (!user || !courseId || !contentId) {
        throw createDataError(
          'Course and content identifiers are required to load this page.',
        );
      }
      const kuduUserId = getKuduUserId(user) ?? user.uid;

      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        // TODO: Replace temporary raw types and legacy MD5 provider-UID auth
        // with protobuf-ts generated types and Firebase ID token verification.
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });
      // The chapter before the student's record. The first chapter read after
      // the due date is the one that closes the chapter on the server and
      // grades the answers saved in it (MaybeCloseComponent): a record read
      // before it showed those answers ungraded, "Score: 0/2", to the student
      // whose page load closed the chapter, until their next load (#103).
      const chapter = await readChapter(
        client,
        courseId,
        contentId,
        isInstructorRequest,
      );
      const response = await client.invokeKuduMethod<
        | RawGetCourseStudentComponentPayload
        | LegacyKuduResponseEnvelope<RawGetCourseStudentComponentPayload>,
        {
          component_id: string;
          course_id: string;
          include_video_sentiment?: boolean;
          is_instructor_request?: boolean;
          user_id: string;
        }
      >('GetCourseStudentComponent', {
        component_id: contentId,
        course_id: courseId,
        ...(isInstructorRequest
          ? { include_video_sentiment: true, is_instructor_request: true }
          : {}),
        user_id: kuduUserId,
      });
      const responsePayload = unwrapStudentComponentPayload(response);
      const component = responsePayload?.component ?? null;

      if (!component) {
        throw createDataError('The component response was missing component data.');
      }

      const hydratedComponent = await hydrateComponentSections(
        client,
        component,
        chapter,
        courseId,
        contentId,
        isInstructorRequest,
      );
      const componentWithEffectiveDueDates = isInstructorRequest
        ? hydratedComponent
        : applyStudentTimeExtensions(
            hydratedComponent,
            await hashUserId(kuduUserId),
          );

      const pageData = normalizeComponentResponse(
        componentWithEffectiveDueDates,
        {
          contentId,
          coursePrelectureQuestionSettings:
            options?.coursePrelectureQuestionSettings,
          courseQuestionSettings: options?.courseQuestionSettings,
          instructorUser: isInstructorRequest
            ? buildInstructorUserForStats(user, kuduUserId)
            : undefined,
          isInstructorRequest,
          kuduUserId,
        },
      );

      if (
        isInstructorRequest ||
        !isChapterClosePending(chapter, pageData.timingState)
      ) {
        return pageData;
      }

      // Counted with the page data, not in the hook: refetchInterval below is
      // worked out again at every update of the query, and a read an
      // invalidation cancels must not count.
      const previous = queryClient.getQueryData<ComponentPageData>(queryKey);

      return {
        ...pageData,
        closePendingReads: (previous?.closePendingReads ?? 0) + 1,
      };
    },
    queryKey: componentQueryKey(
      courseId ?? '',
      contentId ?? '',
      isInstructorRequest,
      questionSettingsCacheKey,
    ),
    // A student's page whose read found the close pending reads again, in
    // the background, until a read finds the chapter complete or the delays
    // run out: each read of a chapter in that state runs the server's close
    // again, grading the whole class. Staff pages never do: an instructor may
    // be editing.
    refetchInterval: (query) => {
      // A read that fails ends them too: it keeps the last page data, and
      // with it the count, so the page read again at the same spacing for as
      // long as reads failed (reviews of PR 140). The page shows its error.
      if (query.state.status === 'error') {
        return false;
      }

      const reads = query.state.data?.closePendingReads ?? 0;

      return reads > 0 ? (CLOSE_PENDING_READ_DELAYS_MS[reads - 1] ?? false) : false;
    },
    retry: false,
  });
}
